import { helpWhy } from '../../help/why';
import React, { useEffect, useState } from 'react';
import {
    Button, Dialog, DialogActions, DialogContent, DialogTitle,
    FormControl, Grid, InputLabel, MenuItem, Select, TextField,
} from '@mui/material';
import { SampleRequestService } from '../../services/sampleRequest.service';
import { BuyerService } from '../../services/buyer.service';
import { StyleService } from '../../services/style.service';
import { AuthService } from '../../services/auth.service';
import { ISampleRequest, SampleType } from '../../interfaces/garments/sampleRequest.interface';
import { IBuyer } from '../../interfaces/garments/buyer.interface';
import { IStyle } from '../../interfaces/garments/style.interface';
import { BuyerAutocomplete } from './BuyerAutocomplete';
import { StyleAutocomplete } from './StyleAutocomplete';
import CommercialPartyAutocomplete from '../commercial/CommercialPartyAutocomplete';
import { useToast } from '../../context/ToastContext';
import { EmployeeAutocomplete, EmployeeOption } from '../common/EmployeeAutocomplete';
import { UserService } from '../../services/user.service';
import { toLocalIsoDate } from '../../utils/dateUtils';
import { SAMPLE_TYPES } from './sampleTypeOptions';
import { enumLabel } from '../../utils/garmentsLabels';

interface Props {
    open: boolean;
    /** Present for edit, null/undefined for a fresh round-1 create. */
    sampleRequest?: ISampleRequest | null;
    onClose: () => void;
    onSaved: (saved: ISampleRequest) => void;
}

/**
 * Create/edit dialog for a sample request. A fresh request always starts round 1 with no parent
 * (`SampleRequestService.create`) — resubmitting an existing round is a distinct action
 * (`SampleRequestList`'s Resubmit button), never a field on this form. Only reachable for
 * REQUESTED/IN_WORK requests when editing (`EDITABLE_SAMPLE_STATUSES`) — the caller is
 * responsible for not opening this dialog on a locked round.
 */
export function SampleRequestForm({ open, sampleRequest, onClose, onSaved }: Props) {
    const isEdit = !!sampleRequest;
    const toast = useToast();

    const [buyer, setBuyer] = useState<IBuyer | null>(null);
    const [style, setStyle] = useState<IStyle | null>(null);
    const [sampleType, setSampleType] = useState<SampleType>('PROTO');
    const [requestedDate, setRequestedDate] = useState('');
    const [dueDate, setDueDate] = useState('');
    const [quantity, setQuantity] = useState('');
    const [buyerComments, setBuyerComments] = useState('');
    const [internalNotes, setInternalNotes] = useState('');
    const [sampleCost, setSampleCost] = useState('');
    const [laborHours, setLaborHours] = useState('');
    const [agentPartyId, setAgentPartyId] = useState<string | null>(null);
    const [assignee, setAssignee] = useState<EmployeeOption | null>(null);
    const [saving, setSaving] = useState(false);
    const [loadingReferences, setLoadingReferences] = useState(false);

    const institutionId = AuthService.getSelectedInstitution() ?? '';

    useEffect(() => {
        if (!open) return;
        if (sampleRequest) {
            setSampleType(sampleRequest.sampleType);
            setRequestedDate(sampleRequest.requestedDate);
            setDueDate(sampleRequest.dueDate ?? '');
            setQuantity(sampleRequest.quantity != null ? String(sampleRequest.quantity) : '');
            setBuyerComments(sampleRequest.buyerComments ?? '');
            setInternalNotes(sampleRequest.internalNotes ?? '');
            setSampleCost(sampleRequest.sampleCost != null ? String(sampleRequest.sampleCost) : '');
            setLaborHours(sampleRequest.laborHours != null ? String(sampleRequest.laborHours) : '');
            setAgentPartyId(sampleRequest.agentPartyId ?? null);
            setAssignee(null);
            if (sampleRequest.assignedToUserId) {
                const assigneeId = sampleRequest.assignedToUserId;
                // The request carries only the assignee's id; look the person up so the picker can show a name.
                UserService.getInstitutionUsers(0, 200, AuthService.getSelectedInstitution())
                    .then(res => {
                        const match = (res?.content ?? []).find((u: EmployeeOption) => u.id === assigneeId);
                        setAssignee(match ?? { id: assigneeId, username: 'Current assignee' });
                    })
                    .catch(() => setAssignee({ id: assigneeId, username: 'Current assignee' }));
            }
            // The DTO carries only buyerId/styleId — MUI's Autocomplete renders the input text as
            // getOptionLabel(value) applied directly to the `value` prop (it does not look the id
            // up in the loaded `options` list), so an id-only stub here would render literal
            // "undefined (undefined)"/"undefined — undefined". Resolve the real objects first.
            setBuyer(null);
            setStyle(null);
            setLoadingReferences(true);
            const institutionId = AuthService.getSelectedInstitution();
            Promise.all([
                BuyerService.getById(institutionId, sampleRequest.buyerId),
                StyleService.getById(institutionId, sampleRequest.styleId),
            ])
                .then(([resolvedBuyer, resolvedStyle]) => {
                    setBuyer(resolvedBuyer);
                    setStyle(resolvedStyle);
                })
                .catch(e => toast.showApiError(e, 'Failed to load buyer/style details'))
                .finally(() => setLoadingReferences(false));
        } else {
            setBuyer(null);
            setStyle(null);
            setSampleType('PROTO');
            setRequestedDate(toLocalIsoDate());
            setDueDate('');
            setQuantity('');
            setBuyerComments('');
            setInternalNotes('');
            setSampleCost('');
            setLaborHours('');
            setAgentPartyId(null);
            setAssignee(null);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, sampleRequest]);

    const handleBuyerChange = (next: IBuyer | null) => {
        setBuyer(next);
        // A style only belongs to one buyer — changing to a different buyer invalidates any
        // selected style (clearing on every change would fight the buyer/style init above).
        setStyle(current => (current && (!next || current.buyerId !== next.id) ? null : current));
    };

    const saveWhy = saving ? null
        : loadingReferences ? 'Wait for the buyer and style to finish loading.'
        : null;

    const handleSave = async () => {
        if (!buyer || !style || !requestedDate) {
            toast.showError('Buyer, style and requested date are required');
            return;
        }
        const institutionId = AuthService.getSelectedInstitution();
        const payload = {
            buyerId: buyer.id,
            styleId: style.id,
            sampleType,
            requestedDate,
            dueDate: dueDate || null,
            quantity: quantity ? Number(quantity) : null,
            buyerComments: buyerComments || null,
            internalNotes: internalNotes || null,
            sampleCost: sampleCost ? Number(sampleCost) : null,
            laborHours: laborHours ? Number(laborHours) : null,
            // Fix round 1: always send the current agentPartyId -- the backend now only
            // re-validates it when it actually changed from the persisted value (mirrors the
            // buyerId/styleId only-on-change precedent), so an unchanged value never 400s even if
            // the referenced agent has since been deactivated.
            agentPartyId,
            assignedToUserId: assignee?.id ?? null,
        };
        setSaving(true);
        try {
            const saved = isEdit
                ? await SampleRequestService.update(institutionId, sampleRequest!.id, payload)
                : await SampleRequestService.create(institutionId, payload);
            toast.showSuccess(isEdit ? 'Sample request updated' : 'Sample request created');
            onSaved(saved);
            onClose();
        } catch (e) {
            toast.showApiError(e, isEdit ? 'Failed to update sample request' : 'Failed to create sample request');
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth PaperProps={{ 'data-help-page': 'merchandising.sampleForm.page' } as any}>
            <DialogTitle>{isEdit ? `Edit Sample Request${sampleRequest ? ` (Round ${sampleRequest.roundNo})` : ''}` : 'New Sample Request'}</DialogTitle>
            <DialogContent data-help-section="merchandising.sampleForm.section.details">
                <Grid container spacing={2} sx={{ mt: 0.5 }}>
                    <Grid size={{ xs: 12, sm: 6 }}>
                        <BuyerAutocomplete data-help="merchandising.sampleForm.field.buyer" value={buyer} onChange={handleBuyerChange} activeOnly required disabled={saving || loadingReferences} />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                        <StyleAutocomplete
                            data-help="merchandising.sampleForm.field.style"
                            value={style}
                            onChange={setStyle}
                            activeOnly
                            buyerId={buyer?.id}
                            required
                            disabled={saving || loadingReferences || !buyer}
                            helperText={!buyer ? 'Pick a buyer first' : undefined}
                        />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                        <FormControl data-help="merchandising.sampleForm.field.sampleType" fullWidth size="small" required disabled={saving}>
                            <InputLabel>Sample Type</InputLabel>
                            <Select label="Sample Type" value={sampleType} onChange={e => setSampleType(e.target.value as SampleType)}>
                                {SAMPLE_TYPES.map(t => <MenuItem key={t} value={t}>{enumLabel(t)}</MenuItem>)}
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                        <TextField
                            data-help="merchandising.sampleForm.field.quantity"
                            label="Quantity" type="number" size="small" fullWidth disabled={saving}
                            value={quantity} onChange={e => setQuantity(e.target.value.replace(/[^0-9]/g, ''))}
                            inputProps={{ min: 0, step: 1 }}
                        />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                        <TextField
                            data-help="merchandising.sampleForm.field.requestedDate"
                            label="Requested Date" type="date" size="small" fullWidth required disabled={saving}
                            InputLabelProps={{ shrink: true }}
                            value={requestedDate} onChange={e => setRequestedDate(e.target.value)}
                        />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                        <TextField
                            data-help="merchandising.sampleForm.field.dueDate"
                            label="Due Date" type="date" size="small" fullWidth disabled={saving}
                            InputLabelProps={{ shrink: true }}
                            value={dueDate} onChange={e => setDueDate(e.target.value)}
                        />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                        <TextField
                            data-help="merchandising.sampleForm.field.sampleCost"
                            label="Sample Cost" type="number" size="small" fullWidth disabled={saving}
                            value={sampleCost} onChange={e => setSampleCost(e.target.value)}
                            inputProps={{ min: 0, step: 0.01 }}
                        />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                        <TextField
                            data-help="merchandising.sampleForm.field.laborHours"
                            label="Labour Hours" type="number" size="small" fullWidth disabled={saving}
                            value={laborHours} onChange={e => setLaborHours(e.target.value)}
                            inputProps={{ min: 0, step: 0.01 }}
                        />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                        <CommercialPartyAutocomplete
                            data-help="merchandising.sampleForm.field.agent"
                            institutionId={institutionId}
                            partyTypes={['BUYING_AGENT']}
                            label="Buying agent (overrides buyer)"
                            value={agentPartyId}
                            onChange={party => setAgentPartyId(party?.id ?? null)}
                            disabled={saving}
                        />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                        <EmployeeAutocomplete
                            data-help="merchandising.sampleForm.field.assignee"
                            label="Assigned to"
                            size="small"
                            value={assignee}
                            onChange={setAssignee}
                            disabled={saving}
                            onError={(err) => toast.showApiError(err, 'Failed to search employees')}
                        />
                    </Grid>
                    <Grid size={{ xs: 12 }}>
                        <TextField
                            data-help="merchandising.sampleForm.field.buyerComments"
                            label="Buyer Comments" fullWidth multiline rows={2} disabled={saving}
                            value={buyerComments} onChange={e => setBuyerComments(e.target.value)}
                            inputProps={{ maxLength: 1000 }}
                        />
                    </Grid>
                    <Grid size={{ xs: 12 }}>
                        <TextField
                            data-help="merchandising.sampleForm.field.internalNotes"
                            label="Internal Notes" fullWidth multiline rows={2} disabled={saving}
                            value={internalNotes} onChange={e => setInternalNotes(e.target.value)}
                            inputProps={{ maxLength: 1000 }}
                        />
                    </Grid>
                </Grid>
            </DialogContent>
            <DialogActions>
                <Button data-help="merchandising.sampleForm.cancel" onClick={onClose} disabled={saving}>Cancel</Button>
                <Button variant="contained" data-help="merchandising.sampleForm.save" {...helpWhy(saveWhy ? { kind: 'status', message: saveWhy } : null)} onClick={handleSave} disabled={saving || loadingReferences}>
                    {isEdit ? 'Save' : 'Create'}
                </Button>
            </DialogActions>
        </Dialog>
    );
}

export default SampleRequestForm;
